import { useState } from "react";
import posthog from "../lib/posthog";

const MEAL_LABEL = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snacks: "Snacks" };

// ── Tiny drag-handle bar ──────────────────────────────────────────────────────
function DragHandle({ appTheme }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", paddingTop: "10px", paddingBottom: "4px" }}>
      <div style={{ width: "36px", height: "4px", borderRadius: "999px", background: appTheme.cardBorder.replace("1px solid ", "") }} />
    </div>
  );
}

// ── Option row ────────────────────────────────────────────────────────────────
function OptionRow({ icon, label, description, onClick, appTheme, ACCENT }) {
  return (
    <button
      onClick={onClick}
      style={{ width: "100%", display: "flex", alignItems: "center", gap: "14px", padding: "14px 20px", background: "none", border: "none", cursor: "pointer", textAlign: "left", borderRadius: "14px", transition: "background 0.15s" }}
      onMouseEnter={e => e.currentTarget.style.background = appTheme.softBackground}
      onMouseLeave={e => e.currentTarget.style.background = "none"}
    >
      <div style={{ width: "44px", height: "44px", borderRadius: "14px", background: appTheme.accentLight, border: appTheme.accentBorder, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: appTheme.primaryText }}>{label}</div>
        <div style={{ fontSize: "13px", color: appTheme.secondaryText, marginTop: "2px" }}>{description}</div>
      </div>
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke={appTheme.secondaryText} strokeWidth="2" strokeLinecap="round">
        <path d="M9 18l6-6-6-6"/>
      </svg>
    </button>
  );
}

// ── Manual entry form ─────────────────────────────────────────────────────────
function ManualForm({ mealType, onBack, onAdd, onSaveMeal, appTheme, isMobile }) {
  const ACCENT = appTheme.accentColor;
  const label = MEAL_LABEL[mealType] || mealType;
  const [name, setName] = useState("");
  const [calories, setCalories] = useState("");
  const [protein, setProtein] = useState("");
  const [carbs, setCarbs] = useState("");
  const [fats, setFats] = useState("");
  const [saveForLater, setSaveForLater] = useState(true);
  const [error, setError] = useState("");

  function handleSubmit() {
    if (!name.trim()) { setError("Please enter a meal name."); return; }
    const meal = {
      name: name.trim(),
      calories: calories ? Number(calories) : null,
      macros: {
        protein: protein ? Number(protein) : null,
        carbs: carbs ? Number(carbs) : null,
        fats: fats ? Number(fats) : null,
      },
      isManual: true,
      custom: true,
      ingredients: [],
      time: null,
      image: null,
    };
    if (saveForLater) {
      // SUPABASE: insert via onSaveMeal (App.js → saved_meals table)
      onSaveMeal?.(meal);
    }
    posthog.capture("meal_added_manually", { meal_name: meal.name });
    onAdd(meal);
  }

  const inputStyle = { width: "100%", boxSizing: "border-box", padding: "12px 14px", borderRadius: "12px", border: appTheme.cardBorder, background: appTheme.inputBackground, fontSize: "15px", color: appTheme.primaryText, outline: "none", fontFamily: "inherit" };
  const smallInput = { ...inputStyle, fontSize: "14px" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "14px 20px 10px" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "22px", color: appTheme.primaryText, padding: 0, lineHeight: 1, fontWeight: "300" }}>‹</button>
        <h2 style={{ margin: 0, fontSize: "17px", fontWeight: "500", fontFamily: appTheme.fontHeading, color: appTheme.primaryText }}>
          Add to {label}
        </h2>
      </div>

      {/* Form */}
      <div className="modal-scroll-hidden" style={{ flex: 1, overflowY: "auto", padding: "0 20px 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
        <div>
          <label style={{ fontSize: "12px", fontWeight: "700", color: appTheme.secondaryText, letterSpacing: "0.06em", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>Meal name *</label>
          <input autoFocus value={name} onChange={e => { setName(e.target.value); setError(""); }} placeholder="e.g. Chicken & rice" style={inputStyle} />
          {error && <div style={{ color: "#E6544C", fontSize: "12px", marginTop: "4px" }}>{error}</div>}
        </div>

        <div>
          <div style={{ fontSize: "12px", fontWeight: "700", color: appTheme.secondaryText, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "6px" }}>Nutrition <span style={{ fontWeight: "400", textTransform: "none", letterSpacing: 0 }}>— optional</span></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {[
              { label: "Calories (kcal)", val: calories, set: setCalories },
              { label: "Protein (g)", val: protein, set: setProtein },
              { label: "Carbs (g)", val: carbs, set: setCarbs },
              { label: "Fats (g)", val: fats, set: setFats },
            ].map(({ label, val, set }) => (
              <div key={label}>
                <div style={{ fontSize: "11px", color: appTheme.secondaryText, marginBottom: "4px" }}>{label}</div>
                <input type="number" inputMode="numeric" min="0" value={val} onChange={e => set(e.target.value)} placeholder="—" style={smallInput} />
              </div>
            ))}
          </div>
        </div>

        {/* Save toggle */}
        <button
          onClick={() => setSaveForLater(p => !p)}
          style={{ display: "flex", alignItems: "center", gap: "12px", background: "none", border: "none", cursor: "pointer", padding: "4px 0", textAlign: "left" }}
        >
          <div style={{ width: "44px", height: "26px", borderRadius: "999px", background: saveForLater ? ACCENT : appTheme.softBackground, border: saveForLater ? "none" : appTheme.cardBorder, position: "relative", flexShrink: 0, transition: "background 0.2s" }}>
            <div style={{ width: "20px", height: "20px", borderRadius: "50%", background: "#fff", position: "absolute", top: "3px", left: "3px", transform: saveForLater ? "translateX(18px)" : "translateX(0)", transition: "transform 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }} />
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: "14px", fontWeight: "600", color: appTheme.primaryText }}>Save for quick access</div>
            <div style={{ fontSize: "12px", color: appTheme.secondaryText }}>Find it instantly next time under "My saved meals"</div>
          </div>
        </button>
      </div>

      {/* CTA */}
      <div style={{ padding: "12px 20px", borderTop: `1px solid rgba(0,0,0,0.06)`, background: appTheme.pageBackground }}>
        <button
          onClick={handleSubmit}
          style={{ width: "100%", padding: "14px", borderRadius: "14px", border: "none", background: ACCENT, color: "#fff", fontWeight: "700", fontSize: "15px", cursor: "pointer" }}
        >
          Add to {label}
        </button>
      </div>
    </div>
  );
}

// ── Saved meals list ──────────────────────────────────────────────────────────
function SavedList({ savedMeals, loading, onSelect, onDelete, onBack, appTheme }) {

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "14px 20px 10px" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "22px", color: appTheme.primaryText, padding: 0, lineHeight: 1, fontWeight: "300" }}>‹</button>
        <h2 style={{ margin: 0, fontSize: "17px", fontWeight: "500", fontFamily: appTheme.fontHeading, color: appTheme.primaryText }}>My saved meals</h2>
      </div>

      <div className="modal-scroll-hidden" style={{ flex: 1, overflowY: "auto", padding: "4px 20px 20px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: appTheme.secondaryText, fontSize: "14px" }}>Loading saved meals…</div>
        ) : savedMeals.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 24px" }}>
            <div style={{ marginBottom: "12px", display: "flex", justifyContent: "center" }}>
              <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.00656 7.5H17.9934C18.218 7.50114 18.4399 7.45186 18.6428 7.35579C18.8458 7.25973 19.0245 7.11933 19.166 6.94496C19.3074 6.77058 19.4079 6.56668 19.4601 6.34829C19.5122 6.12989 19.5146 5.90259 19.4672 5.68313C18.7706 2.39063 15 0 10.5 0C6 0 2.22937 2.39063 1.53281 5.68313C1.48538 5.90259 1.48782 6.12989 1.53995 6.34829C1.59208 6.56668 1.69257 6.77058 1.83401 6.94496C1.97546 7.11933 2.15424 7.25973 2.35718 7.35579C2.56012 7.45186 2.78204 7.50114 3.00656 7.5ZM10.5 1.5C14.2331 1.5 17.4572 3.43219 17.9934 6H3.00656L3 5.99344C3.54281 3.43219 6.76688 1.5 10.5 1.5ZM19.9931 12.045L16.1372 13.4512L12.6562 12.0534C12.4775 11.982 12.2781 11.982 12.0994 12.0534L8.63062 13.4419L5.15625 12.0534C4.9852 11.9851 4.79499 11.9821 4.62188 12.045L0.496875 13.545C0.322534 13.6217 0.183924 13.7619 0.10918 13.9371C0.0344355 14.1124 0.0291573 14.3094 0.0944164 14.4884C0.159676 14.6673 0.290582 14.8148 0.460566 14.9007C0.630549 14.9867 0.826873 15.0048 1.00969 14.9512L2.25 14.5022V15C2.25 15.9946 2.64509 16.9484 3.34835 17.6517C4.05161 18.3549 5.00544 18.75 6 18.75H15C15.9946 18.75 16.9484 18.3549 17.6517 17.6517C18.3549 16.9484 18.75 15.9946 18.75 15V14.0934L20.5059 13.455C20.6044 13.4262 20.6959 13.3775 20.7748 13.3119C20.8537 13.2463 20.9183 13.1652 20.9646 13.0737C21.0109 12.9821 21.0379 12.882 21.044 12.7796C21.05 12.6772 21.035 12.5746 20.9999 12.4782C20.9647 12.3819 20.9102 12.2937 20.8396 12.2193C20.769 12.1448 20.6839 12.0856 20.5896 12.0453C20.4952 12.0051 20.3936 11.9846 20.291 11.9852C20.1884 11.9858 20.087 12.0074 19.9931 12.0488V12.045ZM17.25 15C17.25 15.5967 17.0129 16.169 16.591 16.591C16.169 17.0129 15.5967 17.25 15 17.25H6C5.40326 17.25 4.83097 17.0129 4.40901 16.591C3.98705 16.169 3.75 15.5967 3.75 15V13.9575L4.86281 13.5525L8.34375 14.9466C8.5225 15.018 8.72188 15.018 8.90063 14.9466L12.3694 13.5581L15.8381 14.9466C16.0092 15.0149 16.1994 15.0179 16.3725 14.955L17.2416 14.6391L17.25 15ZM0 9.75C0 9.55109 0.0790176 9.36032 0.21967 9.21967C0.360322 9.07902 0.551088 9 0.75 9H20.25C20.4489 9 20.6397 9.07902 20.7803 9.21967C20.921 9.36032 21 9.55109 21 9.75C21 9.94891 20.921 10.1397 20.7803 10.2803C20.6397 10.421 20.4489 10.5 20.25 10.5H0.75C0.551088 10.5 0.360322 10.421 0.21967 10.2803C0.0790176 10.1397 0 9.94891 0 9.75Z" fill={appTheme.secondaryText}/>
              </svg>
            </div>
            <div style={{ fontSize: "16px", fontWeight: "500", color: appTheme.primaryText, marginBottom: "8px", fontFamily: appTheme.fontHeading }}>No saved meals yet</div>
            <div style={{ fontSize: "14px", color: appTheme.secondaryText, lineHeight: 1.6 }}>Add a meal manually and turn on "Save for quick access" to find it here next time.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {savedMeals.map(meal => (
              <div key={meal.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 0", borderBottom: `1px solid rgba(0,0,0,0.05)` }}>
                <button
                  onClick={() => onSelect(meal)}
                  style={{ flex: 1, background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0 }}
                >
                  <div style={{ fontSize: "14px", fontWeight: "600", color: appTheme.primaryText }}>{meal.name}</div>
                  {meal.calories && <div style={{ fontSize: "12px", color: appTheme.secondaryText, marginTop: "2px" }}>{meal.calories} kcal{meal.protein ? ` · ${meal.protein}g protein` : ""}</div>}
                </button>
                <button
                  onClick={() => onDelete(meal.id)}
                  style={{ width: "30px", height: "30px", borderRadius: "999px", border: "1px solid rgba(220,38,38,0.25)", background: "rgba(220,38,38,0.06)", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "16px" }}
                >×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main sheet ────────────────────────────────────────────────────────────────
function MealChoiceSheet({ show, target, onClose, onBrowseRecipes, onAddMeal, savedManualMeals, savedMealsLoading, onSaveMeal, onDeleteSavedMeal, isMobile, appTheme }) {
  const [view, setView] = useState("choice"); // "choice" | "manual" | "saved"
  const ACCENT = appTheme.accentColor;
  const mealType = target?.mealType || "breakfast";
  const label = MEAL_LABEL[mealType] || mealType;

  function handleClose() {
    setView("choice");
    onClose();
  }

  function handleSelectSaved(meal) {
    posthog.capture("meal_added_manually", { meal_name: meal.name, source: "saved" });
    onAddMeal({
      name: meal.name,
      calories: meal.calories,
      macros: { protein: meal.protein, carbs: meal.carbs, fats: meal.fats },
      isManual: true,
      custom: true,
      ingredients: [],
      time: null,
      image: null,
    });
    setView("choice");
  }

  function handleDeleteSaved(id) {
    onDeleteSavedMeal?.(id); // SUPABASE: delete via App.js → saved_meals table
  }

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{ position: "fixed", inset: 0, zIndex: 1450, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)" }}
      />

      {/* Sheet */}
      <div
        style={{
          position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1451,
          background: appTheme.pageBackground,
          borderRadius: "24px 24px 0 0",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
          overflow: "hidden",
        }}
        onClick={e => e.stopPropagation()}
      >
        <DragHandle appTheme={appTheme} />

        {view === "choice" && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "6px 20px 14px" }}>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "500", fontFamily: appTheme.fontHeading, color: appTheme.primaryText }}>
                Add {label}
              </h2>
              <div style={{ fontSize: "13px", color: appTheme.secondaryText, marginTop: "3px" }}>How would you like to add this meal?</div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", paddingBottom: "20px" }}>
              <OptionRow
                appTheme={appTheme}
                ACCENT={ACCENT}
                label="Browse recipes"
                description="Choose from our recipe collection"
                onClick={onBrowseRecipes}
                icon={<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>}
              />
              <OptionRow
                appTheme={appTheme}
                ACCENT={ACCENT}
                label="Enter manually"
                description="Type in a meal name and nutrition info"
                onClick={() => setView("manual")}
                icon={<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>}
              />
              <OptionRow
                appTheme={appTheme}
                ACCENT={ACCENT}
                label="Quick Add"
                description={savedMealsLoading ? "Loading…" : savedManualMeals.length > 0 ? `${savedManualMeals.length} saved meal${savedManualMeals.length === 1 ? "" : "s"}` : "Re-add meals you eat regularly"}
                onClick={() => setView("saved")}
                icon={<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>}
              />
            </div>
          </div>
        )}

        {view === "manual" && (
          <ManualForm
            mealType={mealType}
            onBack={() => setView("choice")}
            onAdd={(meal) => { onAddMeal(meal); setView("choice"); }}
            onSaveMeal={onSaveMeal}
            appTheme={appTheme}
            isMobile={isMobile}
          />
        )}

        {view === "saved" && (
          <SavedList
            savedMeals={savedManualMeals}
            loading={savedMealsLoading}
            onSelect={meal => { handleSelectSaved(meal); }}
            onDelete={handleDeleteSaved}
            onBack={() => setView("choice")}
            appTheme={appTheme}
          />
        )}
      </div>
    </>
  );
}

export default MealChoiceSheet;
